<template>
  <v-card>
    <HeartbeatDetailsDialog ref="detailsDialog" />
    <ClientVariablesDialog ref="varsDialog" />
    <ExportHbsDialog ref="exportDialog"/>

    <v-toolbar dense class="card-bar">
      <v-toolbar-title>Heartbeats</v-toolbar-title>
      <v-btn icon small class="ml-2" @click="reload()">
        <v-icon>refresh</v-icon>
      </v-btn>
      <v-btn
        icon
        small
        class="ml-2"
        :color="timer != null ? 'primary' : 'transparent'"
        @click="toggleTimer"
      >
        <v-icon size="24" :color="timer == null ? 'rgba(0,0,0,.54)' : ''"
          >mdi-eye-outline</v-icon
        >
      </v-btn>

      <v-btn
        icon
        small
        class="ml-2"
        @click="$refs.varsDialog.open(client.id)"
      >
        <v-icon>mdi-camera-outline</v-icon>
      </v-btn>

      <v-btn icon small class="ml-2" @click="$refs.exportDialog.open(client.id)">
        <v-icon>mdi-download</v-icon>
      </v-btn>

      <v-spacer />

      <!-- <template v-if="!$isMobile">
        <v-switch v-model="hideGlobalstar"
          dense
          label="Ocultar Sat."
          hide-details single-line/>
        <v-spacer/>
      </template> -->

      <Paging
        dense
        :limit="nav.limit"
        :count="count"
        :offset.sync="nav.offset"
      />
    </v-toolbar>

    <OLoadingLine :value="isLoading" absolute />

    <div style="height: 300px; position: relative">
      <HeartbeatsMap :heartbeats="rows" :highlighted="highlightedHb" />
    </div>

    <div class="maxh-4">
      <div class="o-table-overflow">
        <table
          class="v-datatable v-table o-table theme-light fixed-table dense-table"
        >
          <colgroup>
            <col width="140" />
            <col width="120" />
            <col width="80" />
            <col width="80" />
            <col width="110" />
            <col width="80" />
            <col width="110" />
          </colgroup>
          <thead>
            <tr>
              <th class="text-right">ID</th>
              <th class="text-right">Flags</th>
              <th class="text-center">Received</th>
              <th class="text-center">∆T</th>
              <th class="text-right">RD Start</th>
              <th class="text-right">RD Read</th>
              <th class="text-right">RD End</th>
            </tr>
          </thead>
          <tbody v-for="g in groups" :key="g.label">
            <OTBodyHeader :colspan="7">{{ g.label }}</OTBodyHeader>

            <template v-for="row in g.items">
              <tr
                :key="row.id"
                class="hoverable clickable"
                @mouseover="highlightedHb = row"
                @click="(evt) => showDetails(row, evt)"
                @click.middle="(evt) => showDetails(row, evt)"
              >
                <td class="text-right">
                  {{ row.id }}
                  <tt class="reqId">{{ row.summary.reqId }}</tt>
                </td>
                <td>
                  <HeartbeatFlags :heartbeat="row" />
                </td>
                <td class="text-center">
                  {{ row.receivedOn | moment('HH:mm:ss') }}
                </td>
                <td class="text-center grey--text">
                  <span v-if="row.deltaTS != null">
                    {{ row.deltaTS | sDuration }}
                  </span>
                </td>
                <td class="text-right">
                  {{ row.originalLastPointer | brNum }}
                </td>
                <td class="text-right">
                  {{ row.summary.rawDataLength | brNum }}
                </td>
                <td class="text-right">
                  {{ row.summary.endPointer | brNum }}
                </td>
              </tr>

              <tr
                :key="`deltaT_${row.id}`"
                v-if="row.deltaTS > min5"
                class="deltaT"
                :class="row.deltaTS > min15 ? 'lg' : 'md'"
              >
                <td colspan="7">&nbsp;</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </v-card>
</template>

<script>
import moment from 'moment'
import { Heartbeat } from '@/models'
import { groupByDay } from 'oderon-vue/utils'
import { brNum } from 'oderon-commons/src/formatters'
import Paging from '@/components/Paging'
import HeartbeatsMap from '../../../components/heartbeats/map/HeartbeatsMap.vue'
import HeartbeatFlags from '../../../components/heartbeats/flags/HeartbeatFlags.vue'
import HeartbeatDetailsDialog from '../../../components/HeartbeatDetailsDialog'
import OTBodyHeader from 'oderon-vue/cpts/OTBodyHeader.vue'
import ClientVariablesDialog from './ClientVariablesDialog.vue'
import ExportHbsDialog from '../../../components/heartbeats/ExportHbsDialog.vue'

function setDeltas(rows) {
  for (let i = 0; i < rows.length - 1; i++) {
    let row = rows[i],
      prev = rows[i + 1]

    row.deltaTS = moment(row.receivedOn).diff(prev.receivedOn, 's')
  }
}

export default {
  components: {
    Paging,
    HeartbeatDetailsDialog,
    OTBodyHeader,
    HeartbeatsMap,
    HeartbeatFlags,
    ClientVariablesDialog,
    ExportHbsDialog
  },
  props: {
    client: { required: true }
  },
  data() {
    return {
      min15: 15 * 60,
      min5: 5 * 60,
      isLoading: false,
      rows: [],
      groups: [],
      nav: {
        limit: 20,
        offset: 0
      },
      count: null,
      timer: null,
      highlightedHb: null,
      hideGlobalstar: false
    }
  },
  computed: {
    filterParams() {
      return {
        clientId: this.client.id,
        sources: this.hideGlobalstar ? 'OderonFox' : null
      }
    },
    apiParams() {
      return {
        ...this.filterParams,
        limit: this.nav.limit,
        offset: this.nav.offset,
        order: 'receivedOn desc'
      }
    }
  },
  watch: {
    filterParams() {
      this.nav.offset = 0
    },
    apiParams() {
      this.reload()
    }
  },
  mounted() {
    this.reload()
  },
  beforeDestroy() {
    this.stopTimer()
  },
  methods: {
    reload() {
      this.$loading(() => this.fetchData())
    },

    async fetchData() {
      let data = await Heartbeat.findAll(this.apiParams)

      setDeltas(data.rows)

      this.rows = data.rows
      this.count = data.count
      this.groups = groupByDay(this.rows, 'receivedOn')
    },

    startTimer() {
      let delayMs = 5 * 1000
      this.timer = setInterval(() => this.reload(), delayMs)
    },

    stopTimer() {
      if (this.timer) clearInterval(this.timer)
      this.timer = null
    },

    toggleTimer() {
      if (this.timer) this.stopTimer()
      else this.startTimer()
    },

    showDetails(row, evt) {
      if (evt && (evt.ctrlKey || evt.button == 1)) {
        window.open(`/#/heartbeats/${row.id}`, '_blank')
      } else {
        let hbId = row.id
        this.$refs.detailsDialog.open(hbId)
      }
    }
  },
  filters: {
    brNum
  }
}
</script>

<style>
.reqId {
  padding: 2px 4px;
  font-size: 90%;
  color: #c7254e;
  background-color: #f9f2f4;
  border-radius: 4px;
}

.dense-table .deltaT td {
  text-align: center;
  background-color: #f5f5f5;
  padding: 2px !important;
  color: #aaa;
  font-size: 0px;
  box-shadow: inset 0px 4px 6px -4px rgba(0, 0, 0, 0.16);
}

.dense-table .deltaT.md td {
  line-height: 2px !important;
}

.dense-table .deltaT.lg td {
  line-height: 11px !important;
}

.vehicle-marker.border {
  border-width: 2px;
  border-style: solid;
  border-color: black !important;
}
</style>
